import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, Users, TrendingUp, Briefcase, Star } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Careers() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    instagram: '',
    platforms: '',
    follower_count: '',
    niche: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/api/careers/apply`, formData);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    {
      icon: <Users className="w-8 h-8" />,
      title: "Expert Management",
      description: "Work with experienced professionals who understand the influencer landscape."
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Growth Strategy",
      description: "Personalized strategies to grow your audience and engagement across platforms."
    },
    {
      icon: <Briefcase className="w-8 h-8" />,
      title: "Career Support",
      description: "Administrative support, contract review, and business guidance."
    },
    {
      icon: <Star className="w-8 h-8" />,
      title: "Career Development",
      description: "Long-term planning to build a sustainable career in digital content."
    }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center" data-testid="careers-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center glass p-12 max-w-lg mx-4"
        >
          <CheckCircle className="w-16 h-16 text-primary mx-auto mb-6" />
          <h2 className="font-heading text-2xl text-text-primary mb-4">
            Application Received
          </h2>
          <p className="text-text-secondary mb-6">
            Thank you for your interest in joining El Diablo. Our team will review your application 
            and get back to you within 3-5 business days.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                name: '',
                email: '',
                instagram: '',
                platforms: '',
                follower_count: '',
                niche: '',
                message: ''
              });
            }}
            className="btn-secondary"
          >
            Submit Another Application
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24" data-testid="careers-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl text-text-primary mb-6">
              Join Our <span className="text-primary text-glow-red">Roster</span>
            </h1>
            <p className="text-text-secondary text-base md:text-lg">
              Are you a digital creator looking to take your career to the next level? 
              We're always looking for talented individuals to join our roster.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 md:py-20 bg-surface/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl text-text-primary mb-4">
              Why Join <span className="text-primary text-glow-red">El Diablo</span>
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              We provide the tools, connections, and support you need to succeed.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass p-6 text-center hover:border-primary/50 transition-colors"
              >
                <div className="text-primary mb-4 flex justify-center">{benefit.icon}</div>
                <h3 className="font-heading text-lg text-text-primary mb-2">
                  {benefit.title}
                </h3>
                <p className="text-text-secondary text-sm">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-heading text-3xl md:text-4xl text-text-primary mb-4">
                Apply Now
              </h2>
              <p className="text-text-secondary">
                Fill out the form below and our team will review your application.
              </p>
            </motion.div>

            <motion.form
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              onSubmit={handleSubmit}
              className="glass p-8 space-y-6"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-text-primary text-sm font-medium mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-surface border border-border px-4 py-3 text-text-primary placeholder:text-text-secondary/50 focus:border-primary focus:outline-none transition-colors"
                    placeholder="Your full name"
                    data-testid="careers-name"
                  />
                </div>

                <div>
                  <label className="block text-text-primary text-sm font-medium mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-surface border border-border px-4 py-3 text-text-primary placeholder:text-text-secondary/50 focus:border-primary focus:outline-none transition-colors"
                    placeholder="your@email.com"
                    data-testid="careers-email"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-text-primary text-sm font-medium mb-2">
                    Instagram Handle *
                  </label>
                  <input
                    type="text"
                    name="instagram"
                    value={formData.instagram}
                    onChange={handleChange}
                    required
                    className="w-full bg-surface border border-border px-4 py-3 text-text-primary placeholder:text-text-secondary/50 focus:border-primary focus:outline-none transition-colors"
                    placeholder="@yourusername"
                    data-testid="careers-instagram"
                  />
                </div>

                <div>
                  <label className="block text-text-primary text-sm font-medium mb-2">
                    Follower Count *
                  </label>
                  <select
                    name="follower_count"
                    value={formData.follower_count}
                    onChange={handleChange}
                    required
                    className="w-full bg-surface border border-border px-4 py-3 text-text-primary focus:border-primary focus:outline-none transition-colors"
                    data-testid="careers-followers"
                  >
                    <option value="">Select range</option>
                    <option value="1k-10k">1K - 10K</option>
                    <option value="10k-50k">10K - 50K</option>
                    <option value="50k-100k">50K - 100K</option>
                    <option value="100k-500k">100K - 500K</option>
                    <option value="500k-1m">500K - 1M</option>
                    <option value="1m+">1M+</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-text-primary text-sm font-medium mb-2">
                  Primary Platforms *
                </label>
                <input
                  type="text"
                  name="platforms"
                  value={formData.platforms}
                  onChange={handleChange}
                  required
                  className="w-full bg-surface border border-border px-4 py-3 text-text-primary placeholder:text-text-secondary/50 focus:border-primary focus:outline-none transition-colors"
                  placeholder="e.g., Instagram, TikTok, YouTube"
                  data-testid="careers-platforms"
                />
              </div>

              <div>
                <label className="block text-text-primary text-sm font-medium mb-2">
                  Content Niche *
                </label>
                <input
                  type="text"
                  name="niche"
                  value={formData.niche}
                  onChange={handleChange}
                  required
                  className="w-full bg-surface border border-border px-4 py-3 text-text-primary placeholder:text-text-secondary/50 focus:border-primary focus:outline-none transition-colors"
                  placeholder="e.g., Lifestyle, Fashion, Fitness, Tech"
                  data-testid="careers-niche"
                />
              </div>

              <div>
                <label className="block text-text-primary text-sm font-medium mb-2">
                  Tell Us About Yourself *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full bg-surface border border-border px-4 py-3 text-text-primary placeholder:text-text-secondary/50 focus:border-primary focus:outline-none transition-colors resize-none"
                  placeholder="Share your story, goals, and why you'd like to join El Diablo..."
                  data-testid="careers-message"
                />
              </div>

              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="careers-submit"
              >
                {loading ? (
                  <span>Submitting...</span>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Application
                  </>
                )}
              </motion.button>
            </motion.form>
          </div>
        </div>
      </section>
    </div>
  );
}
